import { ViewEncapsulation, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, Input } from '@angular/core';
import { Component, AfterViewInit, OnDestroy } from '@angular/core';
import { Marker, Map } from 'mapbox-gl';
// import { Feature } from 'geojson';

import { MoteEventType, MoteEvent, Coords, Mote, AudioItemEvent, AudioItemId, AudioItem } from '../../models';

// From local motes SDK
import { MapService } from '../../providers';
import { AutoSubOnDestroy, AutoSub } from '../../providers/auto-sub';
import { MoteEventService } from '../../providers/mote-event.service';
import { fromEvent } from 'rxjs';

@AutoSubOnDestroy()
@Component({
    selector: 'augmented-map-marker',
    templateUrl: './augmented-map-marker.component.html',
    styleUrls: ['./augmented-map-marker.component.scss'],
    encapsulation: ViewEncapsulation.None, // Set to None as we cant contain this in component selector because only part of the dom is inserted as mapbox marker
    changeDetection: ChangeDetectionStrategy.Default
})
export class AugmentedMapMarkerComponent implements AfterViewInit, OnDestroy {

    @Input() audioItem: AudioItem = null;
    @Input() mote: Mote = null;
    @Output() moteEvent = new EventEmitter<MoteEvent>();

    @ViewChild('augmentedMarkerContainer', { static: true }) markerContainer: ElementRef;
    @ViewChild('augmentedMapMarker', { static: true }) mapMarker: ElementRef;

    public isOpen = false;
    public markerDiv: HTMLElement;

    private map: Map;
    private mapboxMarker = null;

    private scale: number = 1;
    private rotation: number = 0;
    private bearing: number = 0;

    constructor(private mapService: MapService, private moteEventService: MoteEventService) {
    }

    ngAfterViewInit() {

        // Wait for map to init
        this.mapService.awaitInit().then(map => {
            this.map = map;
            this.markerDiv = this.markerContainer.nativeElement;
            this.initListners(this.mapService);

            // Init map marker right away if component input was initialized with mote and/or audioItem
            if(this.mote || this.audioItem) {
                let position = this.getAugmentedMoteItemPosition(this.mote, this.audioItem);
                this.openMarker(position, 200).then(() => {
                    this.isOpen = true;
                    this.updateMarkerRadius(this.mote);
                });
            }
            console.log(this.mote, this.audioItem);
            // Debug, print to console to easier access in dom
            //console.log(this.markerContainer.nativeElement);
        });
    }

    // Make initListners public for debug purposes
    public initListners(mapService: MapService) {
        let mapZoom = fromEvent(this.mapService.map, 'zoom');

        AutoSub(this).reg['zoomSub'] = mapZoom.subscribe(() => {
            if(!this.isOpen) return;
            this.updateMarkerRadius(this.mote);
        });

        AutoSub(this).reg['rotateSub'] = mapService.watchMapCenter().subscribe((ev) => {
            if(!this.isOpen) return;
            this.rotation = (360 - ev.bearing) - this.mote.rotation;
            this.setRotationAndScale(this.scale, this.rotation);
        });

        AutoSub(this).reg['eventSub'] = this.moteEventService.watchMoteEvents().subscribe((ev: MoteEvent) => {

            switch(ev.type) {
                case MoteEventType.HIDE_3D_MOTE_ON_MAP:
                    // this.isOpen = false;
                    break;
                case MoteEventType.SHOW_3D_MOTE_ON_MAP:
                    break;

                case MoteEventType.ZOOM_TO_3D_MOTE_DONE:
                    console.log("Show 3D mote on map in map marker!!!")
                    // Calculate position from mote position and audio item relative offset
                    let position = this.getAugmentedMoteItemPosition(ev.mote, this.audioItem);
                    this.openMarker(position, 400).then(() => {
                        this.isOpen = true;
                        this.mote = ev.mote;
                        this.rotation = 0;
                        this.updateMarkerRadius(this.mote);
                        this.setRotationAndScale(this.scale, this.rotation);
                    }).catch(() => { });
                    break;

            }
        });

        // Watch map clicks
        // AutoSub(this).reg['clickSub'] = mapService.watchMapClick().subscribe( ev => {
        //     this.mapClick(ev);
        // });

        // AutoSub(this).reg['releaseSub'] = mapService.watchMapRelease().subscribe( ev => {
        //     this.mapRelease(ev);
        // });
    }

    private getAugmentedMoteItemPosition(mote: Mote, audioItem: AudioItem): Coords | null {
        if(!mote) return null;
        let position = mote.position;
        if(!audioItem) return position;

        // If we have a audio item add offset to position
        // For now assume room size 400m
        let offsetX = (1 - audioItem.position.xn - 0.5) * 100;
        let offsetY = (1 - audioItem.position.yn - 0.5) * 100;

        console.log("Offset ", offsetX, offsetY);
        return this.getOffsetCoords(position, offsetX, offsetY);
    }

    private getOffsetCoords(pos: Coords, dy: number, dx: number): Coords {
        // Estimate coords with offset (precise withing 10-10 km)
        // https://stackoverflow.com/questions/2839533/adding-distance-to-a-gps-coordinate
        let lat = pos.lat + (180 / Math.PI) * (dy / 6371000);
        let long = pos.long + (180 / Math.PI) * (dx / 6371000) / Math.cos(pos.lat);
        return new Coords(lat, long, true);
    }


    private updateMarkerRadius(mote: Mote): number {
        if(!this.isOpen || !mote) return;
        // Calculate circle radius
        const currentZoom = this.mapService.map.getZoom();
        const circleWidth = this.metersToPixes(mote.position.lat, 16, currentZoom);
        this.setMarkerRadius(circleWidth);
    }

    public setMarkerRadius(radiusPx: number) {
        if(!this.mapMarker) return;
        this.scale = radiusPx / 60;
        this.setRotationAndScale(this.scale, this.rotation)
    }

    private setRotationAndScale(scale: number, rotation: number) {
        let marker = this.mapMarker.nativeElement;
        marker.style.transform = "rotateX(-60deg) scale(" + scale + ") translateY(0vh) rotateZ(" + rotation + "deg)";
    }

    private metersToPixes(latitude, meters, zoomLevel) {
        return meters / this.metersPerPixel(latitude, zoomLevel);
    }

    private metersPerPixel(latitude, zoomLevel) {
        const earthCircumference = 40075017;
        let latitudeRadians = latitude * (Math.PI / 180);
        return earthCircumference * Math.cos(latitudeRadians) / Math.pow(2, zoomLevel + 8);
    }

    private openMarker(position: Coords, delay = 30): Promise<void> {
        // Do nothing for cluster features
        if(!position) return Promise.reject(null);

        return new Promise(async (resolve, reject) => {
            this.addMapboxMarker(position);
            // Delay open a bit for transition to be recognized
            setTimeout(() => resolve(), delay);
        });
    }

    /**
    *  Add mapbox marker if not already
    */
    private addMapboxMarker(position: Coords) {
        if(!position) return;
        if(!this.mapService.initialized) return;

        if(this.mapboxMarker) {
            this.mapboxMarker.remove();
        }
        this.mapboxMarker = new Marker(this.markerDiv, { offset: [0, 0] });
        this.mapboxMarker.setLngLat(position.longLat()).addTo(this.map);
    }

    // public mapClick(ev:MapClickEvent) {
    // }

    // public mapRelease(ev:MapClickEvent) {
    // }

    // private emitMoteEvent(type:MoteEventType) {
    //     this.moteEvent.emit({mote:null, type:type});
    // }

    public ngOnDestroy() {
        if(this.mapboxMarker) this.mapboxMarker.remove();
    }
}
